/**
 * Precision Planning — Crop Suitability Index  (mvp2_agrotech_precision)
 *
 * Scores each defined field for its suitability to grow high-value crops
 * (Kiwi, Avocado, Tea, Coffee, etc.) based on available satellite-derived
 * data signals.  The score is computed from a multi-factor matrix and
 * presented as a ranked comparison table + per-field detail card.
 *
 * Scoring factors (all derived from existing data):
 *   • NDVI mean (vigour potential)
 *   • NDRE mean (nitrogen / chlorophyll capacity)
 *   • NDWI mean (soil moisture availability)
 *   • Anomaly frequency (negative factor)
 *   • Current crop compatibility bonus (same family → higher base)
 *
 * Data sources (all existing endpoints):
 *  • GET /api/satellite/boundaries          — field list + crop metadata
 *  • GET /api/satellite-imagery/:id/records — satellite index time series
 *  • GET /api/anomalies                     — anomaly history
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart2, RefreshCw, Activity, ZapOff, ChevronDown, ChevronUp,
  CheckCircle2, AlertTriangle, Info,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import {
  getBoundaries, getAllAnomalies, getBatchedImagery, invalidateCache,
  type CachedField, type CachedAnomaly,
} from '../../services/precisionDataCache';
import { useOrgSettings } from '../../hooks/useOrgSettings';

// ─── Types ─────────────────────────────────────────────────────────────────────

type Field = CachedField;

interface IndexMeans {
  NDVI?: number;
  NDRE?: number;
  NDWI?: number;
}

interface CropScore {
  crop: string;
  score: number;         // 0–100
  suitability: 'high' | 'moderate' | 'marginal' | 'unsuitable';
  factors: { label: string; value: number; weight: number; contribution: number }[];
}

interface FieldSuitability {
  field: Field;
  indexMeans: IndexMeans;
  anomalyRate: number;     // anomalies per ha
  scores: CropScore[];
  topCrop: CropScore | null;
  expanded: boolean;
}

// ─── Suitability scoring model ─────────────────────────────────────────────────

const CROP_REQUIREMENTS: Record<string, {
  ndvi_min: number; ndvi_ideal: number;
  ndre_min: number | null;
  ndwi_min: number | null; ndwi_max: number | null;
  familyMatch: string[];
}> = {
  Kiwi:      { ndvi_min: 0.5,  ndvi_ideal: 0.75, ndre_min: 0.1,  ndwi_min: 0.05, ndwi_max: 0.4,  familyMatch: ['kiwi', 'vineyard'] },
  Avocado:   { ndvi_min: 0.45, ndvi_ideal: 0.70, ndre_min: 0.08, ndwi_min: 0.02, ndwi_max: 0.35, familyMatch: ['avocado'] },
  Tea:       { ndvi_min: 0.55, ndvi_ideal: 0.80, ndre_min: 0.12, ndwi_min: 0.05, ndwi_max: 0.5,  familyMatch: ['tea'] },
  Coffee:    { ndvi_min: 0.5,  ndvi_ideal: 0.75, ndre_min: 0.10, ndwi_min: 0.03, ndwi_max: 0.45, familyMatch: ['coffee'] },
  Vineyard:  { ndvi_min: 0.4,  ndvi_ideal: 0.65, ndre_min: 0.07, ndwi_min: 0.01, ndwi_max: 0.3,  familyMatch: ['vineyard', 'kiwi'] },
  Macadamia: { ndvi_min: 0.45, ndvi_ideal: 0.7,  ndre_min: 0.08, ndwi_min: 0.02, ndwi_max: 0.4,  familyMatch: [] },
  Blueberry: { ndvi_min: 0.4,  ndvi_ideal: 0.65, ndre_min: null, ndwi_min: 0.05, ndwi_max: 0.5,  familyMatch: [] },
};

function scoreField(
  indexMeans: IndexMeans,
  anomalyRate: number,
  currentCropType: string | undefined,
  cropKey: string,
): CropScore {
  const req = CROP_REQUIREMENTS[cropKey];
  const ndvi = indexMeans.NDVI ?? 0.45;   // assume moderate if no data
  const ndre = indexMeans.NDRE;
  const ndwi = indexMeans.NDWI;

  const factors: CropScore['factors'] = [];

  // Factor 1: NDVI (weight 40)
  const ndviScore = Math.min(100, Math.max(0, ((ndvi - req.ndvi_min) / (req.ndvi_ideal - req.ndvi_min)) * 100));
  factors.push({ label: 'Canopy vigour (NDVI)', value: +ndvi.toFixed(3), weight: 40, contribution: +(ndviScore * 0.4).toFixed(1) });

  // Factor 2: NDRE (weight 20, if available)
  let ndreContrib = 10;   // neutral if no data
  if (ndre !== undefined && req.ndre_min !== null) {
    const ndreScore = Math.min(100, Math.max(0, ((ndre - req.ndre_min) / 0.15) * 100));
    ndreContrib = +(ndreScore * 0.2).toFixed(1);
  }
  factors.push({ label: 'Nitrogen status (NDRE)', value: ndre ?? NaN, weight: 20, contribution: ndreContrib });

  // Factor 3: Soil moisture (NDWI) (weight 25)
  let ndwiContrib = 12.5;   // neutral if no data
  if (ndwi !== undefined && req.ndwi_min !== null && req.ndwi_max !== null) {
    const inRange = ndwi >= req.ndwi_min && ndwi <= req.ndwi_max;
    ndwiContrib = inRange ? 25 : Math.max(0, 25 - Math.abs(ndwi - (req.ndwi_min + req.ndwi_max) / 2) * 100);
    ndwiContrib = +ndwiContrib.toFixed(1);
  }
  factors.push({ label: 'Soil moisture (NDWI)', value: ndwi ?? NaN, weight: 25, contribution: ndwiContrib });

  // Factor 4: Anomaly penalty (weight 15)
  const anomalyPenalty = Math.min(15, anomalyRate * 5);
  const anomalyContrib = +(15 - anomalyPenalty).toFixed(1);
  factors.push({ label: 'Field health (low anomaly rate)', value: anomalyRate, weight: 15, contribution: anomalyContrib });

  // Crop family match bonus
  const familyBonus = currentCropType && req.familyMatch.some(f => currentCropType.toLowerCase().includes(f)) ? 5 : 0;

  const raw = factors.reduce((s, f) => s + f.contribution, 0) + familyBonus;
  const score = +Math.min(100, Math.max(0, raw)).toFixed(1);

  const suitability: CropScore['suitability'] =
    score >= 70 ? 'high' : score >= 50 ? 'moderate' : score >= 30 ? 'marginal' : 'unsuitable';

  return { crop: cropKey, score, suitability, factors };
}

// ─── Sub-components ────────────────────────────────────────────────────────────

const SUIT_STYLE: Record<CropScore['suitability'], string> = {
  high:       'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  moderate:   'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
  marginal:   'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  unsuitable: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

function ScoreBar({ score }: { score: number }) {
  const colour = score >= 70 ? 'bg-green-500' : score >= 50 ? 'bg-yellow-500' : score >= 30 ? 'bg-orange-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 rounded-full bg-gray-200 dark:bg-gray-700">
        <div className={`h-1.5 rounded-full ${colour}`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-xs text-gray-600 dark:text-gray-400 w-8 text-right">{score}</span>
    </div>
  );
}

function FieldCard({ entry, onToggle }: { entry: FieldSuitability; onToggle: () => void }) {
  const { uom } = useOrgSettings();
  const top = entry.topCrop;

  return (
    <Card>
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div>
            <p className="text-base font-bold text-gray-900 dark:text-white">{entry.field.name}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {entry.field.area_hectares?.toFixed(1) ?? '—'} {uom.area_unit_label}
              {entry.field.metadata?.crop_type && ` · Current: ${entry.field.metadata.crop_type}`}
            </p>
          </div>
          {top && (
            <span className={`text-xs font-semibold px-2 py-0.5 rounded ${SUIT_STYLE[top.suitability]}`}>
              Best: {top.crop} ({top.score}/100)
            </span>
          )}
        </div>

        {/* Top 3 crops bar chart */}
        <div className="space-y-1.5 mb-3">
          {entry.scores.slice(0, 5).map(cs => (
            <div key={cs.crop} className="grid grid-cols-[80px_1fr_auto] items-center gap-2">
              <span className="text-xs text-gray-600 dark:text-gray-400 truncate">{cs.crop}</span>
              <ScoreBar score={cs.score} />
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${SUIT_STYLE[cs.suitability]}`}>
                {cs.suitability}
              </span>
            </div>
          ))}
        </div>

        {/* Index summary */}
        <div className="flex items-center gap-4 flex-wrap text-xs text-gray-500 mb-3">
          <span>NDVI: <strong className="text-gray-700 dark:text-gray-300">{entry.indexMeans.NDVI?.toFixed(3) ?? '—'}</strong></span>
          <span>NDRE: <strong className="text-gray-700 dark:text-gray-300">{entry.indexMeans.NDRE?.toFixed(3) ?? '—'}</strong></span>
          <span>NDWI: <strong className="text-gray-700 dark:text-gray-300">{entry.indexMeans.NDWI?.toFixed(3) ?? '—'}</strong></span>
          {entry.anomalyRate > 0 && (
            <span className="text-amber-600 dark:text-amber-400">
              <AlertTriangle className="inline w-3 h-3 mr-0.5" />
              {entry.anomalyRate.toFixed(2)} anomaly/ha
            </span>
          )}
        </div>

        {/* Expand: factor breakdown */}
        {top && (
          <div className="border-t border-gray-100 dark:border-gray-700 pt-3">
            <button type="button" onClick={onToggle}
              className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex items-center gap-1">
              {entry.expanded ? <><ChevronUp className="w-3.5 h-3.5" />Hide factors</> : <><ChevronDown className="w-3.5 h-3.5" />Show scoring factors</>}
            </button>
            {entry.expanded && (
              <div className="mt-3 space-y-1.5">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                  Scoring factors — {top.crop}
                </p>
                {top.factors.map(f => (
                  <div key={f.label} className="flex items-center justify-between gap-2 text-xs">
                    <span className="text-gray-600 dark:text-gray-400">{f.label}</span>
                    <span className="text-gray-500">{!isNaN(f.value) ? f.value.toFixed(3) : 'n/a'}</span>
                    <span className="text-gray-400">w={f.weight}</span>
                    <span className="font-semibold text-gray-700 dark:text-gray-300">{f.contribution.toFixed(1)} pts</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export const CropSuitabilityPage: React.FC = () => {
  const [entries, setEntries] = useState<FieldSuitability[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  const load = useCallback(async (bust = false) => {
    try {
      setLoading(true);
      setError(null);
      if (bust) invalidateCache();

      // All base datasets share the module-level TTL cache across pages
      const [fields, allAnomalies] = await Promise.all([
        getBoundaries(),
        getAllAnomalies().catch(() => [] as CachedAnomaly[]),
      ]);

      // Anomaly counts by boundary — zero extra requests
      const anomalyCountMap = new Map<string, number>();
      allAnomalies.forEach(a => {
        if (a.boundary_id) {
          anomalyCountMap.set(a.boundary_id, (anomalyCountMap.get(a.boundary_id) ?? 0) + 1);
        }
      });

      // Imagery via shared cache — batched + inter-batch delay
      const imageryMap = await getBatchedImagery(fields, 50);

      const results: FieldSuitability[] = fields.map(field => {
        const records = imageryMap.get(field.id) ?? [];
        const indexMeans: IndexMeans = {};

        if (records.length > 0) {
          const sums: Record<string, number> = {};
          const cnts: Record<string, number> = {};
          records.forEach(r => {
            (r.indices ?? []).forEach((idx: any) => {
              if (typeof idx?.mean_value === 'number') {
                sums[idx.index_type] = (sums[idx.index_type] ?? 0) + idx.mean_value;
                cnts[idx.index_type] = (cnts[idx.index_type] ?? 0) + 1;
              }
            });
          });
          if (cnts['NDVI']) indexMeans.NDVI = +(sums['NDVI'] / cnts['NDVI']).toFixed(4);
          if (cnts['NDRE']) indexMeans.NDRE = +(sums['NDRE'] / cnts['NDRE']).toFixed(4);
          if (cnts['NDWI']) indexMeans.NDWI = +(sums['NDWI'] / cnts['NDWI']).toFixed(4);
        }

        const areaHa = field.area_hectares ?? 1;
        const anomalyRate = +((anomalyCountMap.get(field.id) ?? 0) / areaHa).toFixed(3);

        try {
          const currentCrop = field.metadata?.crop_type;
          const scores = Object.keys(CROP_REQUIREMENTS)
            .map(crop => scoreField(indexMeans, anomalyRate, currentCrop, crop))
            .sort((a, b) => b.score - a.score);
          return { field, indexMeans, anomalyRate, scores, topCrop: scores[0] ?? null, expanded: false };
        } catch {
          return { field, indexMeans, anomalyRate, scores: [], topCrop: null, expanded: false };
        }
      });

      results.sort((a, b) => (b.topCrop?.score ?? 0) - (a.topCrop?.score ?? 0));
      setEntries(results);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load suitability data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (idx: number) =>
    setEntries(prev => prev.map((e, i) => i === idx ? { ...e, expanded: !e.expanded } : e));

  const highCount = entries.filter(e => e.topCrop?.suitability === 'high').length;

  return (
    <div className="space-y-6">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <BarChart2 className="w-7 h-7 text-teal-600" /> Crop Suitability Index
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Multi-factor suitability scoring for high-value crop transition — scored from satellite NDVI, NDRE, NDWI, and anomaly history
          </p>
        </div>
        <button type="button" onClick={() => load(true)} disabled={loading}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* ── Summary ────────────────────────────────────────────────────────── */}
      {!loading && entries.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Fields Assessed', value: entries.length,    colour: 'text-gray-700 dark:text-gray-300' },
            { label: 'High Suitability', value: highCount,         colour: 'text-green-600' },
            { label: 'Moderate',         value: entries.filter(e => e.topCrop?.suitability === 'moderate').length, colour: 'text-yellow-600' },
            { label: 'Marginal / Poor',  value: entries.filter(e => e.topCrop?.suitability === 'marginal' || e.topCrop?.suitability === 'unsuitable').length, colour: 'text-orange-600' },
          ].map(({ label, value, colour }) => (
            <Card key={label} className="p-4 text-center">
              <p className={`text-2xl font-bold ${colour}`}>{value}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
            </Card>
          ))}
        </div>
      )}

      {/* ── Info note ──────────────────────────────────────────────────────── */}
      <div className="rounded-lg bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-700/40 p-4 flex gap-3">
        <Info className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-teal-700 dark:text-teal-300 leading-relaxed">
          <strong>Scoring model:</strong> NDVI vigour (40%) · NDRE nitrogen (20%) · NDWI moisture (25%) · Anomaly frequency (15%).
          Scores ≥70 = High suitability. Crop family bonus (+5) applied when current crop is related.
          Model does not include soil laboratory data or elevation — use as a first-pass screening tool only.
        </div>
      </div>

      {/* ── Content ────────────────────────────────────────────────────────── */}
      {error && <Alert type="error">{error}</Alert>}

      {loading ? (
        <div className="flex justify-center py-16"><Spinner size="lg" /></div>
      ) : entries.length === 0 ? (
        <Card className="p-10 text-center">
          <ZapOff className="mx-auto w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No fields found</p>
          <p className="text-xs text-gray-400 mt-1">Define fields and run temporal analysis to generate suitability scores.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {entries.map((entry, idx) => (
            <FieldCard key={entry.field.id} entry={entry} onToggle={() => toggle(idx)} />
          ))}
        </div>
      )}
    </div>
  );
};

export default CropSuitabilityPage;
// Made with Bob
